import { useEffect } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { groupColor } from './mapColors';
import type { MapLocation } from '@/types';

interface LocationToastProps {
  location: MapLocation;
  onClose: () => void;
  isExplored: boolean;
  navigatorUnlocked: boolean;
  isTravelTarget: boolean;
  onSetTarget?: () => void;
  onClearTarget?: () => void;
  autoHideMs?: number;
}

export function LocationToast({
  location,
  onClose,
  isExplored,
  navigatorUnlocked,
  isTravelTarget,
  onSetTarget,
  onClearTarget,
  autoHideMs = 5000,
}: LocationToastProps) {
  useEffect(() => {
    if (!autoHideMs) return;
    const t = setTimeout(onClose, autoHideMs);
    return () => clearTimeout(t);
  }, [location.id, autoHideMs, onClose]);

  const canTarget = navigatorUnlocked && isExplored;

  return (
    <div
      className={cn(
        'absolute left-1/2 -translate-x-1/2 z-10 w-[min(360px,calc(100%-1rem))]',
        'glass rounded-2xl p-3 shadow-card animate-slide-up',
      )}
      style={{
        top: 'calc(max(env(safe-area-inset-top, 0px), var(--tg-content-safe-area-top, 0px), var(--tg-safe-area-top, 0px)) + 80px)',
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-3 h-3 rounded-full mt-1.5 shrink-0"
          style={{ background: groupColor(location.group) }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-display text-base text-white truncate">
              {location.name}
            </h3>
            <button
              onClick={onClose}
              className="text-white/40 hover:text-white text-xl leading-none w-6 h-6 inline-flex items-center justify-center"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          {location.description && (
            <p className="text-xs text-white/70 leading-snug mt-1 line-clamp-3">
              {location.description}
            </p>
          )}
          {location.specialCondition && (
            <div className="flex gap-2 mt-2 text-[10px]">
              {location.specialCondition.level && (
                <span className="px-2 py-0.5 rounded-md bg-gold/15 text-gold">
                  Lv. {location.specialCondition.level}+
                </span>
              )}
              {location.specialCondition.item && (
                <span className="px-2 py-0.5 rounded-md bg-stardust/15 text-stardust">
                  Требуется: {location.specialCondition.item}
                </span>
              )}
            </div>
          )}
          {canTarget && (
            <div className="mt-2">
              {isTravelTarget ? (
                <Button size="sm" variant="secondary" onClick={onClearTarget}>
                  Отменить цель
                </Button>
              ) : (
                <Button size="sm" onClick={onSetTarget}>
                  Назначить целью
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
